import { useObservable } from '../observable';
import { appStateService } from '../app.state.service';
import useApi from 'hooks/useApi';
import { BootResponse, OrderData } from 'constants/apiResponses.types';
import { useMemo } from 'react';
import { EFeatureFlag, EOfferType, EQueryParams } from 'constants/enums';
import useUrlQuery from 'hooks/useUrlQuery';

const CAMPAIGN_SOURCE = 'campaign';

export const useAppState = () => {
  const API = useApi({});
  const publisherMetaData = API.getPublisherMeta.data as BootResponse;
  const from = useUrlQuery(EQueryParams.FROM);
  const isFromCampaign = useMemo(() => from === CAMPAIGN_SOURCE, [from]);

  const productsAnimationPurchase = useMemo(
    () => publisherMetaData?.featureFlags?.store_animation_after_purchases,
    [publisherMetaData?.featureFlags?.store_animation_after_purchases]
  );

  const productsAnimationCollect = useMemo(
    () =>
      publisherMetaData?.featureFlags[
        EFeatureFlag.STORE_COLLECT_ANIMATION_POST_FREE_COLLECT_PROMOTIONS_POPUPS
      ],
    [
      publisherMetaData?.featureFlags[
        EFeatureFlag.STORE_COLLECT_ANIMATION_POST_FREE_COLLECT_PROMOTIONS_POPUPS
      ],
    ]
  );

  const shouldDisplayProductsAnimation = (order: OrderData): boolean => {
    const price = order.price;
    const isRollingStyleOffer =
      order.offerType === EOfferType.ROLLING_OFFER ||
      order.offerType === EOfferType.ONE_PLUS_MANY;

    return (
      isRollingStyleOffer || (price === 0 ? productsAnimationCollect : productsAnimationPurchase)
    );
  };

  const openInfoModal = (url?: string) => {
    appStateService.setInfoModalUrl(url);
  };

  const closeInfoModal = () => {
    appStateService.setInfoModalUrl();
  };

  return {
    collectedProductsImages: useObservable(appStateService.collectedProductsImages),
    collectedPointsImages: useObservable(appStateService.collectedPointsImages),
    displayLoader: useObservable(appStateService.displayLoader),
    showConfetti: useObservable(appStateService.showConfetti),
    isProfileEnabled: useObservable(appStateService.isProfileEnabled),
    selectedOffer: useObservable(appStateService.selectedOffer),
    failedHashValidation: useObservable(appStateService.failedHashValidation),
    installPrompt: useObservable(appStateService.installPrompt),
    backgroundProps: useObservable(appStateService.backgroundProps),
    infoModalUrl: useObservable(appStateService.infoModalUrl),
    utms: useObservable(appStateService.utms),
    isTabFooterDisplayed: useObservable(appStateService.isTabFooterDisplayed),
    isFromCampaign,
    setCollectedProductsImages: appStateService.setCollectedProductsImages,
    setCollectedPointsImages: appStateService.setCollectedPointsImages,
    setDisplayLoader: appStateService.setDisplayLoader,
    setShowConfetti: appStateService.setShowConfetti,
    setIsProfileEnabled: appStateService.setIsProfileEnabled,
    setSelectedOffer: appStateService.setSelectedOffer,
    setFailedHashValidation: appStateService.setFailedHashValidation,
    setBackgroundProps: appStateService.setBackgroundProps,
    setUtms: appStateService.setUtms,
    openInfoModal,
    closeInfoModal,
    shouldDisplayProductsAnimation,
    setIsTabFooterDisplayed: appStateService.setIsTabFooterDisplayed,
    setInstallPrompt: appStateService.setInstallPrompt,
  };
};
